import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { resumeService, getBlobErrorMessage } from '../services/api';

import type { ResumeRequest } from '../types';
import type { TemplateItem } from '../services/api';
import { useSubscription } from '../hooks/useSubscription';
import toast from 'react-hot-toast';
import { Download, Eye, LayoutGrid, Lock, X } from 'lucide-react';

interface LivePreviewProps {
  data: ResumeRequest;
  selectedTemplate: string;
  onSelectTemplate: (template: string) => void;
  onClose?: () => void;
  activeSection?: string;
}

const RESUME_API_URL = import.meta.env.VITE_RESUME_API_URL || 'https://3atech.de/api/v1/resumes';
const THUMB_ORIGIN = RESUME_API_URL.replace(/\/api\/v1\/resumes$/, '');
const PICKER_PAGE_SIZE = 12;

const LivePreview: React.FC<LivePreviewProps> = ({
  data,
  selectedTemplate,
  onSelectTemplate,
  onClose,
  activeSection,
}) => {
  const [allTemplates, setAllTemplates] = useState<TemplateItem[]>([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [pickerOffset, setPickerOffset] = useState(PICKER_PAGE_SIZE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const modalIframeRef = useRef<HTMLIFrameElement | null>(null);
  const navigate = useNavigate();
  const { isPro, loading: subLoading } = useSubscription();

  const scrollToActiveSection = useCallback((iframe: HTMLIFrameElement | null) => {
    if (!iframe || !activeSection) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const keywords: Record<string, string[]> = {
        personal: ['contact', 'personal info', 'personal profile', 'profile', 'about me'],
        summary: ['professional summary', 'career objective', 'objective', 'summary'],
        experience: ['work experience', 'professional experience', 'employment history', 'work history', 'experience'],
        education: ['education'],
        skills: ['technical skills', 'core competencies', 'skills', 'expertise'],
      };
      const sectionKeywords = keywords[activeSection] || [];
      const matchesKeyword = (text: string) => {
        const t = text.trim().toLowerCase();
        return sectionKeywords.some(k => t === k || t.startsWith(k + ' ') || t.startsWith(k + '\n'));
      };

      let target: HTMLElement | null = null;
      for (const el of Array.from(doc.querySelectorAll<HTMLElement>('h1,h2,h3,h4,[class*="title"]'))) {
        if (matchesKeyword(el.textContent || '')) {
          target = el;
          break;
        }
      }

      if (!target) {
        const classHint: Record<string, string> = {
          personal: 'contact',
          summary: 'summary',
          experience: 'experience',
          education: 'education',
          skills: 'skill',
        };
        const hint = classHint[activeSection];
        if (hint) {
          for (const el of Array.from(doc.querySelectorAll<HTMLElement>('[class]'))) {
            if ((el.getAttribute('class') || '').toLowerCase().includes(hint)) {
              target = el;
              break;
            }
          }
        }
      }

      if (target) {
        if (!doc.querySelector('style[data-scroll-sync]')) {
          const style = doc.createElement('style');
          style.setAttribute('data-scroll-sync', '');
          style.textContent = '.scroll-sync-target { scroll-margin-top: 12px; }';
          doc.head?.appendChild(style);
        }
        target.classList.add('scroll-sync-target');
        target.scrollIntoView({ block: 'start' });
      }
    } catch {
      // Ignore any cross-origin / rendering errors; scrolling is best-effort.
    }
  }, [activeSection]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const response = await resumeService.getTemplates(0, 100);
        if (cancelled) return;
        if (response.data?.templates) {
          setAllTemplates(response.data.templates);
          if (!response.data.templates.some(t => t.id === selectedTemplate) && response.data.templates.length > 0) {
            onSelectTemplate(response.data.templates[0].id);
          }
        }
      } catch {
        if (cancelled) return;
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedTemplate || allTemplates.length === 0) return;
    if (timerRef.current) clearTimeout(timerRef.current);

    setLoading(true);
    const delay = hasLoadedOnce ? 800 : 300;

    timerRef.current = setTimeout(async () => {
      try {
        const response = await resumeService.previewHtml({
          ...data,
          templateId: selectedTemplate,
        });
        setPreviewHtml(response.data);
        setHasLoadedOnce(true);
      } catch {
        // keep old content (empty if never loaded)
      }
      setLoading(false);
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, selectedTemplate, allTemplates.length]);

  const handleGeneratePdf = useCallback(async () => {
    const templateInfo = allTemplates.find(t => t.id === selectedTemplate);
    if (templateInfo && !templateInfo.free && !isPro) {
      if (subLoading) {
        toast.error('Checking your subscription...');
        return;
      }
      toast.error('This is a Pro template. Upgrade to download it.');
      navigate('/pricing');
      return;
    }
    setIsGenerating(true);
    try {
      const response = await resumeService.generateResume({
        ...data,
        templateId: selectedTemplate,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.personalInfo?.fullName || 'Resume'}_${selectedTemplate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded successfully!');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        toast.error('This is a Pro template. Upgrade to download it.');
        navigate('/pricing');
      } else if (status === 503) {
        toast.error('Payments are temporarily unavailable. You can download once payments are re-enabled and you have an active subscription.');
      } else {
        toast.error(await getBlobErrorMessage(error));
      }
    } finally {
      setIsGenerating(false);
    }
  }, [data, selectedTemplate, allTemplates, isPro, subLoading, navigate]);

  const selectedTemplateInfo = allTemplates.find(t => t.id === selectedTemplate);
  const isSelectedPremium = selectedTemplateInfo ? !selectedTemplateInfo.free && !isPro : false;

  const templateDisplay = selectedTemplateInfo?.name
    || (selectedTemplate
      ? selectedTemplate.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Select template');

  const openPicker = useCallback(() => {
    setPickerOffset(PICKER_PAGE_SIZE);
    setShowTemplatePicker(true);
  }, []);

  const handleSelect = useCallback((id: string) => {
    onSelectTemplate(id);
    setShowTemplatePicker(false);
  }, [onSelectTemplate]);

  const handleLoadMore = useCallback(() => {
    setPickerOffset(prev => Math.min(prev + PICKER_PAGE_SIZE, allTemplates.length));
  }, [allTemplates.length]);

  const visibleTemplates = allTemplates.slice(0, pickerOffset);

  const getThumbUrl = (templateId: string) => `${THUMB_ORIGIN}/api/v1/resumes/thumbnails/${templateId}`;

  return (
    <div className="flex flex-col h-full">
      {onClose && (
        <div className="flex items-center justify-between px-4 pt-4 pb-2 lg:hidden">
          <span className="text-xs font-semibold text-zinc-400 tracking-wider uppercase">Preview</span>
          <button
            className="w-7 h-7 flex items-center justify-center rounded-btn text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="px-4 pt-2 pb-3 space-y-2 shrink-0">
        <button
          className="w-full h-14 px-3 border border-zinc-200 rounded-btn bg-white flex items-center gap-3 hover:border-zinc-300 transition-colors group"
          onClick={openPicker}
        >
          <div className="w-9 h-12 rounded overflow-hidden bg-zinc-100 shrink-0 border border-zinc-100">
            <img
              src={getThumbUrl(selectedTemplate)}
              alt={templateDisplay}
              className="w-full h-full object-cover object-top"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-semibold text-foreground truncate">{templateDisplay}</p>
            <p className="text-[11px] text-zinc-400">
              {selectedTemplateInfo?.free ? 'Free Template' : 'Pro Template'}
            </p>
          </div>
          <LayoutGrid className="w-4 h-4 text-zinc-400 shrink-0 group-hover:text-zinc-600 transition-colors" />
        </button>

        <div className="flex gap-2">
          <button
            className="flex-1 h-9 px-3 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-medium rounded-btn transition-colors inline-flex items-center justify-center gap-1.5"
            onClick={() => setPreviewModal(true)}
            disabled={!previewHtml}
          >
            <Eye className="w-3.5 h-3.5" />
            Full View
          </button>
          <button
            className={`flex-1 h-9 px-3 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded-btn transition-colors inline-flex items-center justify-center gap-1.5 ${isSelectedPremium ? 'bg-amber-500 hover:bg-amber-600' : 'bg-brand-500 hover:bg-brand-600'}`}
            onClick={handleGeneratePdf}
            disabled={isGenerating}
          >
            {isSelectedPremium ? <Lock className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
            {isSelectedPremium ? 'PRO' : isGenerating ? '...' : 'PDF'}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-4 pb-4">
        <div className="h-full border border-zinc-200 rounded-card bg-white overflow-hidden">
          {!hasLoadedOnce ? (
            <div className="flex items-center justify-center h-full">
              {loading ? (
                <div className="w-5 h-5 border-[3px] border-zinc-200 border-t-brand-500 rounded-full animate-spin" />
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 px-4">
                  <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-zinc-300" />
                  </div>
                  <p className="text-xs text-zinc-400 text-center">Select a template to preview</p>
                </div>
              )}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={previewHtml}
              title="Resume Preview"
              sandbox="allow-same-origin"
              onLoad={() => scrollToActiveSection(iframeRef.current)}
              className="w-full h-full border-none"
            />
          )}
        </div>
      </div>

      <AnimatePresence>
        {previewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6"
            onClick={() => setPreviewModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-card w-full max-w-[1000px] max-h-[90vh] flex flex-col overflow-hidden shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 shrink-0">
                <h2 className="text-lg font-display font-semibold text-foreground">{templateDisplay}</h2>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-btn text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
                  onClick={() => setPreviewModal(false)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-auto bg-zinc-100 p-4">
                <iframe
                  ref={modalIframeRef}
                  srcDoc={previewHtml}
                  title="Full Preview"
                  sandbox="allow-same-origin"
                  onLoad={() => scrollToActiveSection(modalIframeRef.current)}
                  className="w-full h-[600px] border-none bg-white shadow-soft"
                />
              </div>
              <div className="flex justify-center gap-3 px-6 py-4 border-t border-zinc-200">
                <button
                  className="h-10 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                  onClick={() => { handleGeneratePdf(); setPreviewModal(false); }}
                  disabled={isGenerating}
                >
                  <Download className="w-4 h-4" />
                  {isGenerating ? 'Generating...' : 'Download PDF'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTemplatePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6"
            onClick={() => setShowTemplatePicker(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-card w-full max-w-[900px] max-h-[85vh] flex flex-col overflow-hidden shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 shrink-0">
                <h2 className="text-lg font-display font-semibold text-foreground">Choose a Template</h2>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-btn text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
                  onClick={() => setShowTemplatePicker(false)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {visibleTemplates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleSelect(t.id)}
                      className={`text-left bg-white border rounded-card overflow-hidden transition-all duration-200 hover:shadow-elevated hover:-translate-y-0.5 group ${t.id === selectedTemplate ? 'ring-2 ring-brand-500 border-brand-200' : 'border-zinc-200'}`}
                    >
                      <div className="relative aspect-[3/4] bg-zinc-50 overflow-hidden">
                        <img
                          src={getThumbUrl(t.id)}
                          alt={t.name}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '';
                            (e.target as HTMLImageElement).classList.add('opacity-0');
                          }}
                        />
                        {!t.free && (
                          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm text-amber-300 text-[9px] font-semibold px-1.5 py-0.5 rounded z-10">
                            <Lock className="w-2.5 h-2.5" /> PRO
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-xs font-semibold text-foreground truncate">{t.name}</p>
                          {t.free ? (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0 leading-none">Free</span>
                          ) : (
                            <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0 leading-none inline-flex items-center gap-0.5">
                              <Lock className="w-2.5 h-2.5" /> PRO
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {pickerOffset < allTemplates.length && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={handleLoadMore}
                      className="h-9 px-5 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                    >
                      Load More Templates
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LivePreview;
